import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { FiSearch, FiImage, FiLayout, FiInbox, FiMapPin } from "react-icons/fi";
import { getInteriorCatalogs, getInteriorCatalog } from "../../../api/customer/overviewApis";
import useAsyncData from "../dashboard/sections/useAsyncData";
import "../dashboard/sections/OverviewSections.css";

const assetUrl = process.env.REACT_APP_ASSET_URL || "";
const resolveImage = (path) => (!path ? null : /^https?:\/\//i.test(path) ? path : `${assetUrl}${path}`);
const stripHtml = (html) => String(html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
const formatPrice = (value) => {
  const n = parseFloat(value);
  return n ? `₹${n.toLocaleString("en-IN")}` : String(value || "");
};

const useToken = () => {
  const authState = useSelector((state) => state.auth);
  const user = authState?.user ?? authState;
  return user?.refreshToken || authState?.refreshToken || user?.token || authState?.token;
};

const Thumb = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  return (
    <div className="ov-media" style={{ aspectRatio: "16 / 9" }}>
      {src && !failed ? (
        <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />
      ) : (
        <div className="ov-media__placeholder" role="img" aria-label={`${alt} (no image available)`}>
          <FiImage size={28} aria-hidden="true" />
        </div>
      )}
    </div>
  );
};

/** /customer/interior-design — all catalogs (same /interior/catalogs call as mobile) */
const InteriorList = () => {
  const token = useToken();
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState("");
  const { data, loading, error, retry } = useAsyncData(
    () => getInteriorCatalogs(token, { page_size: 50, keyword_search: applied }),
    [token, applied],
    Boolean(token)
  );
  const catalogs = data || [];

  return (
    <div className="ov-page">
      <div>
        <h1 className="ov-section__title" style={{ fontSize: "1.5rem" }}>Interior Design</h1>
        <p className="ov-section__desc">Design catalogs from interior partners.</p>
      </div>

      <form
        className="d-flex gap-2"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied(search.trim());
        }}
      >
        <div className="position-relative flex-grow-1" style={{ maxWidth: 480 }}>
          <FiSearch className="position-absolute" style={{ left: 12, top: 12 }} aria-hidden="true" />
          <input
            type="search"
            className="form-control ps-5"
            placeholder="Search interior designs"
            aria-label="Search interior designs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button type="submit" className="btn btn-primary">Search</button>
      </form>

      {loading ? (
        <div className="ov-grid" aria-hidden="true">
          {[0, 1, 2, 3].map((n) => (
            <div key={n} className="ov-skel-card" style={{ flexBasis: "auto" }}>
              <div className="ov-skel ov-skel--img" />
              <div className="ov-skel ov-skel--line" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="ov-state ov-state--error" role="alert">
          <div>
            <div className="ov-state__title">Couldn't load interior designs</div>
            <div className="ov-state__text">{error}</div>
          </div>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={retry}>Retry</button>
        </div>
      ) : catalogs.length === 0 ? (
        <div className="ov-state">
          <div className="ov-empty">
            <div className="ov-empty__icon" aria-hidden="true"><FiInbox size={24} /></div>
            <div className="ov-empty__title">{applied ? "No matching designs" : "No interior designs yet"}</div>
            <div className="ov-empty__text">{applied ? "Try a different search." : "New catalogs will appear here."}</div>
          </div>
        </div>
      ) : (
        <div className="ov-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
          {catalogs.map((c) => (
            <article key={c.id} className="ov-card" style={{ flexBasis: "auto" }}>
              <Link to={`/customer/interior-design/${c.id}`} className="ov-card__link" aria-label={`View ${c.type?.name || "interior design"}`}>
                <Thumb src={resolveImage(c.catalog_images?.[0]?.image_url)} alt={c.type?.name || "Interior design"} />
                <div className="ov-card__body">
                  <h3 className="ov-card__title">{c.type?.name || "Interior"}</h3>
                  <p className="ov-card__clamp ov-card__clamp--2">{stripHtml(c.description)}</p>
                  {c.price ? <p className="ov-card__price">{formatPrice(c.price)}</p> : null}
                </div>
              </Link>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

/** /customer/interior-design/:Id — one catalog (GET /interior/catalog/details/{id}) */
const InteriorDetail = ({ id }) => {
  const token = useToken();
  const { data: catalog, loading, error, retry } = useAsyncData(() => getInteriorCatalog(token, id), [token, id], Boolean(token));

  if (loading) {
    return (
      <div className="ov-page" aria-busy="true">
        <div className="ov-skel" style={{ height: 28, width: "30%" }} />
        <div className="ov-skel" style={{ aspectRatio: "16 / 6" }} />
      </div>
    );
  }
  if (error || !catalog) {
    return (
      <div className="ov-page">
        <div className="ov-state ov-state--error" role="alert">
          <div>
            <div className="ov-state__title">Couldn't load this design</div>
            <div className="ov-state__text">{error || "It may have been removed."}</div>
          </div>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={retry}>Retry</button>
        </div>
      </div>
    );
  }

  const images = (catalog.catalog_images || []).map((i) => resolveImage(i.image_url)).filter(Boolean);
  const partner = catalog.partner;
  const partnerName = [partner?.first_name, partner?.last_name].filter(Boolean).join(" ");
  const place = [catalog.city, catalog.state].filter(Boolean).join(", ");

  return (
    <div className="ov-page">
      <div>
        <h1 className="ov-section__title" style={{ fontSize: "1.5rem" }}>
          <FiLayout className="me-2 text-primary" aria-hidden="true" />
          {catalog.type?.name || "Interior Design"}
        </h1>
        {catalog.price ? <p className="ov-card__price mt-1">{formatPrice(catalog.price)}</p> : null}
        {place && (
          <p className="ov-card__meta mt-1">
            <FiMapPin size={13} aria-hidden="true" /> <span>{place}</span>
          </p>
        )}
        {partnerName && <p className="ov-section__desc">By {partnerName}</p>}
      </div>

      {images.length > 0 ? (
        <div className="ov-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
          {images.map((src, i) => (
            <Thumb key={src} src={src} alt={`${catalog.type?.name || "Interior"} photo ${i + 1}`} />
          ))}
        </div>
      ) : (
        <Thumb src={null} alt="Interior design" />
      )}

      {catalog.description && (
        <div className="bg-white border rounded-3 p-3" style={{ whiteSpace: "pre-wrap" }}>
          {stripHtml(catalog.description)}
        </div>
      )}
    </div>
  );
};

const CustomerInteriorDesign = () => {
  const { Id } = useParams();
  return Id ? <InteriorDetail id={Id} /> : <InteriorList />;
};

export default CustomerInteriorDesign;
